import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { MessageSquare, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { feedbackService, type FeedbackMessage, type HelpChatResponse } from '@/services/feedbackService';

interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  body: string;
  createdAt: string;
  escalated?: boolean;
}

export function HelpChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [escalated, setEscalated] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading || escalated) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      body: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      let currentTicketId = ticketId;

      // Create ticket on first message
      if (!currentTicketId) {
        const ticket = await feedbackService.createTicket({
          type: 'help',
          subject: text.slice(0, 100),
          metadata: {
            pageUrl: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
        });
        currentTicketId = ticket.id;
        setTicketId(ticket.id);
      }

      // Send message and get AI response
      const result = await feedbackService.sendMessage(currentTicketId, text) as HelpChatResponse;

      const aiMessage: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'ai',
        body: result.response,
        createdAt: new Date().toISOString(),
        escalated: result.escalated,
      };
      setMessages((prev) => [...prev, aiMessage]);

      if (result.escalated) {
        setEscalated(true);
      }
    } catch (err: any) {
      const errorMessage: ChatMessage = {
        id: `err_${Date.now()}`,
        role: 'ai',
        body: 'Sorry, something went wrong. Please try again or use the Bug Report form.',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[400px]">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-3 pr-1">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-8">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
            <p>Ask me anything about Cascady!</p>
            <p className="text-xs mt-1 text-muted-foreground/70">I can help with features, usage, and troubleshooting.</p>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-primary-600 text-white'
                  : msg.escalated
                    ? 'bg-warning-50 border border-warning-200 text-warning-900'
                    : 'bg-muted text-foreground'
              }`}
            >
              {msg.escalated && (
                <div className="flex items-center gap-1.5 mb-1 text-xs font-medium text-warning-700">
                  <Mail className="w-3.5 h-3.5" />
                  Forwarded to support team
                </div>
              )}
              {msg.role === 'ai' ? (
                <div className="chat-markdown">
                  <ReactMarkdown>{msg.body}</ReactMarkdown>
                </div>
              ) : (
                <p className="whitespace-pre-wrap">{msg.body}</p>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-4 py-2.5">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-input pt-3 mt-auto">
        {escalated ? (
          <div className="text-center text-sm text-muted-foreground py-2">
            Our support team will follow up via email. Thanks for reaching out!
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your question..."
              className="flex-1 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none max-h-[80px]"
              rows={1}
              disabled={isLoading}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="flex-shrink-0 p-2"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
